import { Head, Link, router } from '@inertiajs/react'
import { useState } from "react"
import {
  CollectSessionAreaOptionType,
  CollectSessionPaymentType,
  CollectSessionTaxPayerType,
  CollectSessionType,
  PaginationType
} from './types'
import {ArrowLeft, Clock, CreditCard, Printer, Trash2, User} from "lucide-react";
import Pagination from "@/components/pagination.tsx";

interface ShowProps {
  collect_session: CollectSessionType
  payments: CollectSessionPaymentType[]
  payments_pagination: PaginationType
  tax_payers: CollectSessionTaxPayerType[]
  tax_payers_pagination: PaginationType
  areas: CollectSessionAreaOptionType[]
  assigned_area_ids: string[]
  assigned_areas: CollectSessionAreaOptionType[]
  can_assign_areas?: boolean
  can_destroy?: boolean
}

export default function Show({
  collect_session,
  payments,
  payments_pagination,
  tax_payers,
  tax_payers_pagination,
  areas,
  assigned_area_ids,
  assigned_areas,
  can_assign_areas = false,
  can_destroy = false
}: ShowProps) {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false)
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false)
  const [isAssignZonesOpen, setIsAssignZonesOpen] = useState(false)
  const [physicalAmountInput, setPhysicalAmountInput] = useState(
    collect_session.physical_amount_collected ? String(collect_session.physical_amount_collected) : ""
  )
  const [noteInput, setNoteInput] = useState(collect_session.note || "")
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>(assigned_area_ids || [])

  const durationLabel = () => {
    if (!collect_session.start_at || !collect_session.end_at) {
      return "En cours"
    }

    const start = new Date(collect_session.start_at).getTime()
    const end = new Date(collect_session.end_at).getTime()

    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
      return "En cours"
    }

    const totalMinutes = Math.floor((end - start) / 60000)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours <= 0) {
      return `${minutes}m`
    }

    return `${hours}h ${minutes}m`
  }

  const isReconciled = collect_session.status === "reconciled"
  const totalCollectedAmount = Number(collect_session.total_collected_amount || 0)
  const physicalAmount = Number(collect_session.physical_amount_collected || 0)
  const differenceAmount = isReconciled ? Number((physicalAmount - totalCollectedAmount).toFixed(2)) : 0
  const hasDifference = isReconciled

  const escapeHtml = (str: string) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")

  const formatMoney = (value: number | string) =>
    `${Number(value || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA`

  const formatDate = (value?: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString("fr-FR")
  }

  const printSessionStatement = () => {
    const printedAt = new Date()
    const statusLabel = collect_session.status === "open" ? "Ouverte" : collect_session.status === "closed" ? "Fermée" : "Réconciliée"

    const paymentRows = payments
      .map(
        (p) => `
        <tr>
          <td>${escapeHtml(p.tax_payer_name || "Inconnu")}</td>
          <td>${escapeHtml(p.payment_method || "cash")}</td>
          <td>${escapeHtml(formatDate(p.collected_at))}</td>
          <td class="amount">${escapeHtml(formatMoney(p.amount))}</td>
        </tr>`
      )
      .join("")

    const areaLabels = assigned_areas.map((a) => `${escapeHtml(a.name)}${a.code ? ` (${escapeHtml(a.code)})` : ""}`).join(", ")

    const billWindow = window.open("", "_blank", "width=960,height=720")
    if (!billWindow) return

    billWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Relevé de session - ${escapeHtml(collect_session.code)}</title>
          <style>
            * { box-sizing: border-box; }
            body { color: #111827; font-family: Arial, sans-serif; margin: 32px; }
            header { border-bottom: 2px solid #111827; display: flex; justify-content: space-between; gap: 24px; padding-bottom: 18px; }
            h1 { font-size: 24px; margin: 0 0 8px; }
            h2 { font-size: 16px; margin: 24px 0 10px; }
            p { margin: 3px 0; }
            .muted { color: #4b5563; font-size: 12px; }
            .grid { display: grid; gap: 18px; grid-template-columns: 1fr 1fr; margin-top: 22px; }
            .box { border: 1px solid #d1d5db; padding: 14px; }
            .summary-grid { display: grid; gap: 14px; grid-template-columns: repeat(3, 1fr); margin-top: 22px; }
            .stat { border: 1px solid #d1d5db; padding: 14px; text-align: center; }
            .stat p:first-child { color: #4b5563; font-size: 12px; margin: 0 0 6px; }
            .stat p:last-child { font-size: 22px; font-weight: 700; margin: 0; }
            table { border-collapse: collapse; margin-top: 10px; width: 100%; }
            th, td { border: 1px solid #d1d5db; font-size: 12px; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f3f4f6; }
            .amount { text-align: right; white-space: nowrap; }
            footer { border-top: 1px solid #d1d5db; color: #4b5563; font-size: 11px; margin-top: 36px; padding-top: 12px; }
            @media print { body { margin: 18mm; } button, .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <header>
            <div>
              <h1>Relevé de session de collecte</h1>
              <p><strong>${escapeHtml(collect_session.code)}</strong> — <span>${escapeHtml(statusLabel)}</span></p>
              <p class="muted">Genéré le ${escapeHtml(printedAt.toLocaleString("fr-FR"))}</p>
            </div>
          </header>

          <section class="grid">
            <div class="box">
              <h2>Agent</h2>
              <p><strong>Nom:</strong> ${escapeHtml(collect_session.agent_name || "-")}</p>
              <p><strong>Code:</strong> ${escapeHtml(collect_session.agent_code || "-")}</p>
            </div>
            <div class="box">
              <h2>Terminal</h2>
              <p><strong>Nom:</strong> ${escapeHtml(collect_session.device_name || "-")}</p>
              <p><strong>Code:</strong> ${escapeHtml(collect_session.device_code || "-")}</p>
            </div>
          </section>

          <section class="grid">
            <div class="box">
              <h2>Durée</h2>
              <p><strong>Durée:</strong> ${escapeHtml(durationLabel())}</p>
              <p><strong>Début:</strong> ${escapeHtml(formatDate(collect_session.start_at))}</p>
              <p><strong>Fin:</strong> ${escapeHtml(formatDate(collect_session.end_at))}</p>
            </div>
            <div class="box">
              <h2>Zones assignées</h2>
              <p>${areaLabels || "Aucune zone assignée."}</p>
            </div>
          </section>

          <section class="summary-grid">
            <div class="stat">
              <p>Total paiements</p>
              <p>${collect_session.total_payments_count || 0}</p>
            </div>
            <div class="stat">
              <p>Montant total collecté</p>
              <p>${escapeHtml(formatMoney(collect_session.total_collected_amount || 0))}</p>
            </div>
            ${collect_session.status === "reconciled" ? `
            <div class="stat">
              <p>Montant physique déclaré</p>
              <p>${escapeHtml(formatMoney(collect_session.physical_amount_collected || 0))}</p>
            </div>` : ""}
          </section>

          <section>
            <h2>Paiements (${payments.length})</h2>
            <table>
              <thead>
                <tr>
                  <th>Contribuable</th>
                  <th>Mode</th>
                  <th>Date</th>
                  <th class="amount">Montant</th>
                </tr>
              </thead>
              <tbody>
                ${paymentRows || '<tr><td colspan="4">Aucun paiement.</td></tr>'}
              </tbody>
            </table>
          </section>

          <footer>
            Document généré depuis Taxio pour le relevé de session de collecte.
          </footer>

          <script>
            window.addEventListener("load", () => {
              window.print();
              window.close();
            });
          </script>
        </body>
      </html>
    `)
    billWindow.document.close()
  }

  const handleDestroy = () => {
    if (!window.confirm(`Supprimer la session "${collect_session.code}" ?`)) return

    router.delete(`/collect_sessions/${collect_session.id}`)
  }


  return (
    <>
      <Head title={`Collect session #${collect_session.id}`} />

      <div className="p-8">
        <div className="mb-6">
          <Link href="/collect_sessions">
            <button className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Retour
            </button>
          </Link>

          <div className="flex items-start justify-between gap-4 ">
            <div>
              <h2 className="text-3xl font-bold text-foreground">Détails de la session </h2>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-muted-foreground">Code:</span>
                <span className="font-mono text-foreground">{collect_session.code}</span>
                <span
                    className={`rounded-full px-3 py-1 text-sm font-medium ${
                        collect_session.status === "Active"
                            ? "bg-success/10 text-success"
                            : collect_session.status === "Closed"
                                ? "bg-muted text-muted-foreground"
                                : "bg-primary/10 text-primary"
                    }`}
                >
            {collect_session.status}
          </span>
              </div>
            </div>
           <div className={" flex gap-4"}>
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
                onClick={printSessionStatement}
              >
                <Printer className="h-4 w-4" />
                Imprimer
              </button>
              {collect_session.status === "open" && (
                 <button
                     className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
                     onClick={() => setIsCloseModalOpen(true)}
                 >
                   Fermer Session
                 </button>
             )}
             {collect_session.status === "closed" && (
                 <button
                     className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                     onClick={() => setIsReconcileModalOpen(true)}
                 >
                   Reconcile Session
                 </button>
             )}
            {can_assign_areas && (
              <button
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
                onClick={() => setIsAssignZonesOpen(true)}
              >
                Assigner des zones
              </button>
            )}
            {can_destroy && (
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
                onClick={handleDestroy}
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </button>
            )}
          </div>
        </div>
        </div>

        {/* Session Status Card */}
        <div className="mb-6 rounded-lg border border-border bg-card p-6">

          <div className="grid gap-6 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Agent</p>
                <p className="mt-1 font-semibold text-foreground">{collect_session.agent_name}</p>
                <p className="text-xs text-muted-foreground">{collect_session.agent_code || "-"}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <CreditCard className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">POS Device</p>
                <p className="mt-1 font-semibold text-foreground">{collect_session.device_name}</p>
                <p className="text-xs font-mono text-muted-foreground">
                  {collect_session.device_code || "-"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <Clock className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="mt-1 font-semibold text-foreground">{durationLabel()}</p>
                <p className="text-xs text-muted-foreground">
                  Début: {collect_session.start_at ? new Date(collect_session.start_at).toLocaleString() : "-"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Fin: {collect_session.end_at ? new Date(collect_session.end_at).toLocaleString() : "-"}
                </p>
              </div>
            </div>
          </div>

          <div className={`mb-6 grid gap-4 mt-2 ${isReconciled ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            <div className="rounded-lg border border-border/60 bg-background p-4">
              <p className="text-xs text-muted-foreground">Total paiements</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">
                {collect_session.total_payments_count || 0}
              </p>
            </div>
            <div className="rounded-lg border border-border/60 bg-background p-4">
              <p className="text-xs text-muted-foreground">Montant total collecté</p>
              <p className="mt-2 text-2xl font-semibold text-success">
                {Number(collect_session.total_collected_amount || 0).toFixed(2)} FCFA
              </p>
            </div>
              {isReconciled && (
                <div className="rounded-lg border border-border/60 bg-background p-4">
                  <p className="text-xs text-muted-foreground">Montant physique</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">
                    {physicalAmount.toFixed(2)} FCFA
                  </p>
                  {hasDifference && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Écart: <span className={differenceAmount >= 0 ? "text-success" : "text-destructive"}>
                      {differenceAmount >= 0 ? "+" : "-"}
                        {Math.abs(differenceAmount).toFixed(2)} FCFA
                    </span>
                      </p>
                  )}
                  {collect_session.note && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Note: {collect_session.note}
                    </p>
                  )}
                </div>
            )}
          </div>

          <div className="rounded-lg border border-border/60 bg-background p-4">
            <p className="text-xs text-muted-foreground">Zones assignées</p>
            {assigned_areas.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Aucune zone assignée.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {assigned_areas.map((area) => (
                  <span
                    key={area.id}
                    className="rounded-full bg-secondary px-2 py-1 text-xs font-medium text-foreground"
                  >
                    {area.name}{area.code ? ` (${area.code})` : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Contribuables en impayé</h3>
            <p className="text-sm text-muted-foreground">{tax_payers_pagination.total_count} contribuable(s) avec solde restant</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border">
              <tr className="text-left">
                <th className="p-4 text-sm font-medium text-muted-foreground">Contribuable</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Code</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Zone</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Téléphone</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Adresse</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Solde</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Statut</th>
              </tr>
              </thead>
              <tbody>
              {tax_payers.length > 0 ? tax_payers.map((taxPayer) => (
                <tr key={taxPayer.id} className="border-b border-border transition-colors hover:bg-secondary/50">
                  <td className="p-4">
                    <Link href={`/tax_payers/${taxPayer.id}`} className="font-medium text-primary hover:underline">
                      {taxPayer.name || "Contribuable sans nom"}
                    </Link>
                  </td>
                  <td className="p-4 text-sm text-foreground">{taxPayer.code || "-"}</td>
                  <td className="p-4 text-sm text-muted-foreground">{taxPayer.tax_payer_type_name || "-"}</td>
                  <td className="p-4 text-sm text-muted-foreground">{taxPayer.area_name || "-"}</td>
                  <td className="p-4 text-sm text-muted-foreground">{taxPayer.phone || "-"}</td>
                  <td className="p-4 text-sm text-muted-foreground">{taxPayer.address || "-"}</td>
                  <td className="p-4 text-sm font-semibold">
                    <span className={taxPayer.has_outstanding_balance ? "text-warning" : "text-success"}>
                      {Number(taxPayer.balance_due || 0).toFixed(2)} FCFA
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                      taxPayer.has_outstanding_balance ? "bg-warning/10 text-warning" : "bg-success/10 text-success"
                    }`}>
                      {taxPayer.has_outstanding_balance ? "Outstanding" : "Cleared"}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td className="p-4 text-sm text-muted-foreground" colSpan={8}>
                    Aucun contribuable avec solde impayé trouvé pour cette session.
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <Pagination
              currentPage={tax_payers_pagination.current_page}
              totalPages={tax_payers_pagination.total_pages}
              basePath={`/collect_sessions/${collect_session.id}`}
              pageParam="tax_payers_page"
              query={{ payments_page: payments_pagination.current_page }}
            />
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Paiements de la session</h3>
            <p className="text-sm text-muted-foreground">{payments_pagination.total_count} paiements</p>
          </div>

          <div className="space-y-3">
            {payments.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                Aucun paiement trouvé pour cette session.
              </div>
            )}

            {payments.map((payment) => (
              <div key={payment.id} className="rounded-lg border border-border p-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{payment.tax_payer_name || "Contribuable inconnu"}</p>
                    <p className="text-sm text-muted-foreground">
                      Agent : {payment.agent_name || "Inconnu"} • Terminal: {payment.device_name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {payment.payment_method || "cash"} • {new Date(payment.collected_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-success">{Number(payment.amount || 0).toFixed(2)} FCFA</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Pagination
              currentPage={payments_pagination.current_page}
              totalPages={payments_pagination.total_pages}
              basePath={`/collect_sessions/${collect_session.id}`}
              pageParam="payments_page"
              query={{ tax_payers_page: tax_payers_pagination.current_page }}
            />
          </div>
        </div>

        {isCloseModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4"
            onClick={() => setIsCloseModalOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground">Fermer la session</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Voulez-vous vraiment fermer cette session ? Cette action est définitive.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-secondary"
                  onClick={() => setIsCloseModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    setIsCloseModalOpen(false)
                    router.patch(`/collect_sessions/${collect_session.id}/close`)
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {isReconcileModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4"
            onClick={() => setIsReconcileModalOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground">Rapprocher la session</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Saisissez le montant physique collecté pour finaliser le rapprochement.
                </p>
              </div>
              <div className="mb-6">
                <label htmlFor="physical_amount" className="mb-2 block text-sm font-medium text-foreground">
                  Montant physique
                </label>
                <input
                  id="physical_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  value={physicalAmountInput}
                  onChange={(e) => setPhysicalAmountInput(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="reconcile_note" className="mb-2 block text-sm font-medium text-foreground">
                  Note
                </label>
                <textarea
                  id="reconcile_note"
                  rows={3}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Ajouter une note (optionnel)"
                />
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-secondary"
                  onClick={() => setIsReconcileModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  onClick={() => {
                    setIsReconcileModalOpen(false)
                    router.patch(`/collect_sessions/${collect_session.id}/reconcile`, {
                      collect_session: {
                        physical_amount_collected: physicalAmountInput,
                        note: noteInput
                      }
                    })
                  }}
                  disabled={physicalAmountInput.trim() === ""}
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}

        {isAssignZonesOpen && can_assign_areas && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4"
            onClick={() => setIsAssignZonesOpen(false)}
          >
            <div
              className="w-full max-w-lg rounded-lg bg-background p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground">Assigner des zones</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Sélectionnez les zones à associer à cette session.
                </p>
              </div>

              <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
                {areas.length === 0 && (
                  <p className="text-sm text-muted-foreground">Aucune zone disponible.</p>
                )}
                {areas.map((area) => (
                  <label key={area.id} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border"
                      checked={selectedZoneIds.includes(area.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedZoneIds((prev) => [...prev, area.id])
                        } else {
                          setSelectedZoneIds((prev) => prev.filter((id) => id !== area.id))
                        }
                      }}
                    />
                    <span>
                      {area.name}{area.code ? ` (${area.code})` : ""}
                    </span>
                  </label>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-secondary"
                  onClick={() => setIsAssignZonesOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  onClick={() => {
                    setIsAssignZonesOpen(false)
                    router.patch(`/collect_sessions/${collect_session.id}/assign_areas`, {
                      area_ids: selectedZoneIds
                    })
                  }}
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}




      </div>
    </>
  )
}
